/**
 * Elkhart Community Schools district calendar.
 *
 * Encoded from the district's officially approved PDF rather than fetched.
 * The district website (finalsite) publishes no iCal or RSS feed — the
 * endpoints advertised as "Calendar RSS" return nothing, and the calendar
 * grid itself is rendered client-side, so there is nothing stable to sync
 * against. Scraping it would break the first time they restyle the page.
 *
 * Source: "ECS Calendar 2026-2027, Approved as presented by the Board of
 * School Trustees 12/16/2025"
 * https://www.elkhart.k12.in.us/district/calendar
 *
 * TO UPDATE: when the district publishes the next school year, add another
 * entry to SCHOOL_YEARS below. Dates are inclusive on both ends.
 */
export const SCHOOL_YEARS = [
    {
        label: '2026–2027',
        approved: '2025-12-16',
        entries: [
            // --- Fall semester (85 days) ---
            {
                start: '2026-08-10',
                end: '2026-08-12',
                name: 'Teacher Pre-Session Days',
                kind: 'no-students',
            },
            { start: '2026-08-13', name: 'First day of school', kind: 'first-last' },
            { start: '2026-09-07', name: 'Labor Day — buildings closed', kind: 'closed' },
            { start: '2026-10-09', name: 'End of 1st grading period', kind: 'grading' },
            {
                start: '2026-10-19',
                end: '2026-10-20',
                name: 'Parent/teacher conferences (evenings)',
                kind: 'conference',
            },
            { start: '2026-10-21', name: 'eLearning day — staff PD', kind: 'elearning' },
            { start: '2026-10-21', name: 'Parent/teacher conferences (morning)', kind: 'conference' },
            {
                start: '2026-10-22',
                end: '2026-10-26',
                name: 'Fall Recess — schools closed',
                kind: 'closed',
            },
            { start: '2026-11-03', name: 'eLearning day — staff PD', kind: 'elearning' },
            {
                start: '2026-11-25',
                end: '2026-11-27',
                name: 'Thanksgiving Recess — schools closed',
                kind: 'closed',
            },
            { start: '2026-12-18', name: 'End of 2nd grading period / semester', kind: 'grading' },
            {
                start: '2026-12-21',
                end: '2027-01-01',
                name: 'Winter Recess — schools closed',
                kind: 'closed',
            },
            // --- Spring semester (95 days) ---
            { start: '2027-01-04', name: 'School resumes', kind: 'event' },
            {
                start: '2027-01-18',
                name: 'Martin Luther King Jr. Day — buildings closed',
                kind: 'closed',
            },
            {
                start: '2027-02-12',
                end: '2027-02-15',
                name: "Presidents' Recess — schools closed",
                kind: 'closed',
            },
            {
                start: '2027-03-03',
                name: 'eLearning day — staff PD (SAT testing in person)',
                kind: 'elearning',
            },
            { start: '2027-03-12', name: 'End of 3rd grading period', kind: 'grading' },
            { start: '2027-03-26', name: 'Built-in snow day', kind: 'event' },
            {
                start: '2027-04-05',
                end: '2027-04-09',
                name: 'Spring Break — schools closed',
                kind: 'closed',
            },
            { start: '2027-04-15', name: 'Kindergarten Kick-off (evening)', kind: 'event' },
            { start: '2027-05-27', name: 'Last day of school for students', kind: 'first-last' },
            { start: '2027-05-31', name: 'Memorial Day — buildings closed', kind: 'closed' },
        ],
    },
];
export const DISTRICT_NAME = 'Elkhart Community Schools';
/** Muted colours per kind, drawn from the app palette. */
export const DISTRICT_KIND_COLORS = {
    closed: '#C08B8B',
    'no-students': '#C4A287',
    'first-last': '#7C8A5F',
    elearning: '#8E9AAF',
    conference: '#9C7A93',
    grading: '#D9A85C',
    event: '#B07C63',
};
function nextDay(key) {
    const [y, m, d] = key.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
/** Every district marker falling inside a date range, one per day. */
export function districtEventsInRange(from, to) {
    const marks = [];
    for (const year of SCHOOL_YEARS) {
        for (const entry of year.entries) {
            const last = entry.end ?? entry.start;
            // Skip entries entirely outside the window before expanding them.
            if (last < from || entry.start > to)
                continue;
            const multiDay = last !== entry.start;
            let cursor = entry.start;
            let guard = 0;
            while (cursor <= last && guard++ < 400) {
                if (cursor >= from && cursor <= to) {
                    marks.push({
                        date: cursor,
                        name: entry.name,
                        kind: entry.kind,
                        color: DISTRICT_KIND_COLORS[entry.kind],
                        multiDay,
                    });
                }
                cursor = nextDay(cursor);
            }
        }
    }
    return marks.sort((a, b) => (a.date < b.date ? -1 : 1));
}
/** The school year covering a date, if any — used for the footer note. */
export function schoolYearFor(date) {
    for (const year of SCHOOL_YEARS) {
        const first = year.entries[0]?.start;
        const last = year.entries[year.entries.length - 1]?.end ?? year.entries[year.entries.length - 1]?.start;
        if (first && last && date >= first && date <= last)
            return year;
    }
    return null;
}
//# sourceMappingURL=district.js.map